import {getTestProps} from '@benchling/js-test-utils';
import {Badge, createStyles, Group, MantineTheme, Progress, Stack, Text, Title, Tooltip} from '@mantine/core';
import moment from 'moment';
import React from 'react';

import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface CreditUsageBarProps {
  creditsUsed: number;
  // Null when credit limits are not enforced for the tenant.
  creditLimit: number | null;
  // Current billing period start date (UTC)
  periodStart: Date;
  // Next billing period start date, when usage resets (UTC)
  resetDate: Date;
  isTenantOutOfCredits?: boolean;
}

const useStyles = createStyles((theme) => ({
  // Undo the Badge uppercase/bold text default and shrink the label.
  creditsTag: {
    textTransform: 'none',
    fontWeight: theme.other.fontWeights.normal,
    fontSize: theme.fontSizes.sm,
  },
}));

function formatPeriod(periodStart: Date, resetDate: Date): string {
  const periodEnd = moment.utc(resetDate).subtract(1, 'day');
  return `${moment.utc(periodStart).format('MMMM D')} - ${periodEnd.format('D, YYYY')}`;
}

function formatResetsIn(resetDate: Date): string {
  const daysUntilReset = moment.utc(resetDate).diff(moment.utc().startOf('day'), 'days');
  return daysUntilReset === 1 ? 'Resets in 1 day' : `Resets in ${daysUntilReset} days`;
}

function formatCreditsTag(creditsRemaining: number): string {
  const count = Math.abs(creditsRemaining).toLocaleString();
  return creditsRemaining < 0 ? `${count} credits past` : `${count} credits left`;
}

function formatUsagePercentage(usagePercentage: number): string {
  return `${Math.floor(usagePercentage)}% Usage`;
}

function getProgressColor(theme: MantineTheme, isOverLimit: boolean, isTenantOutOfCredits: boolean): string {
  if (isTenantOutOfCredits) {
    return theme.colors.blue[4];
  }
  return isOverLimit ? theme.colors.orange[4] : 'blue';
}

/** Bar showing credits used against a credit limit for the current billing period. */
export default function CreditUsageBar({
  creditsUsed,
  creditLimit,
  periodStart,
  resetDate,
  isTenantOutOfCredits = false,
}: CreditUsageBarProps): JSX.Element {
  const {classes, theme} = useStyles();

  const roundedUsage = Math.round(creditsUsed);
  const hasLimit = creditLimit != null;
  const usagePercentage = hasLimit && creditLimit > 0 ? (creditsUsed / creditLimit) * 100 : 0;
  const creditsRemaining = hasLimit ? creditLimit - roundedUsage : 0;
  const isOverLimit = hasLimit && creditsRemaining < 0;

  return (
    <Stack spacing="sm" {...getTestProps({component: 'CreditUsageBar', element: 'root'})}>
      <Group position="apart" align="flex-start" noWrap={true} spacing="lg">
        <Group spacing="sm" align="baseline" noWrap={true}>
          <Title
            order={3}
            weight={600}
            c={isTenantOutOfCredits && !hasLimit ? 'dimmed' : undefined}
            {...getTestProps({component: 'CreditUsageBar', element: 'usage'})}
          >
            {hasLimit
              ? `${roundedUsage.toLocaleString()} / ${creditLimit.toLocaleString()}`
              : roundedUsage.toLocaleString()}
          </Title>
          <Text weight={600}>credits used</Text>
        </Group>
        {hasLimit && (
          <Badge
            variant="light"
            color={isOverLimit ? 'orange' : 'gray'}
            size="sm"
            radius="sm"
            className={classes.creditsTag}
            leftSection={isOverLimit ? <BenchlingIcon icon="warning" aria-hidden={true} /> : undefined}
            {...getTestProps({component: 'CreditUsageBar', element: 'creditsTag'})}
          >
            {formatCreditsTag(creditsRemaining)}
          </Badge>
        )}
      </Group>

      {hasLimit && (
        <Tooltip
          label={formatUsagePercentage(usagePercentage)}
          position="top"
          events={{focus: true, hover: true, touch: false}}
          withinPortal={true}
          zIndex={ZINDEX_FLOATING_UI}
        >
          <Progress
            tabIndex={0}
            value={Math.min(usagePercentage, 100)}
            color={getProgressColor(theme, isOverLimit, isTenantOutOfCredits)}
            size="md"
            radius="sm"
            aria-label="Credit usage"
            {...getTestProps({component: 'CreditUsageBar', element: 'progress'})}
          />
        </Tooltip>
      )}

      <Text size="sm" c="dimmed" {...getTestProps({component: 'CreditUsageBar', element: 'period'})}>
        {formatPeriod(periodStart, resetDate)}
        {' · '}
        {formatResetsIn(resetDate)}
      </Text>
    </Stack>
  );
}
